import { Component } from '@theme/component';

/**
 * @typedef {Object} MenuDrawerRefs
 * @property {HTMLButtonElement} trigger - The header button that opens the drawer
 * @property {HTMLDialogElement} dialog - The modal drawer
 */

/**
 * Lazariée mobile navigation drawer. A native modal dialog supplies focus containment,
 * Escape to close and focus return to the trigger; this component opens it, keeps
 * `aria-expanded` in sync, closes on backdrop clicks, and closes when the viewport
 * reaches the desktop layout.
 *
 * @extends {Component<MenuDrawerRefs>}
 */
class LazarieeMenuDrawer extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.dialog.addEventListener('close', this.#handleClose, { signal });
    window.matchMedia('(min-width: 750px)').addEventListener(
      'change',
      (event) => {
        if (event.matches) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /** Opens the drawer as a modal dialog. */
  open() {
    const { dialog, trigger } = this.refs;
    if (dialog.open) return;

    dialog.showModal();
    trigger.setAttribute('aria-expanded', 'true');
  }

  /** Closes the drawer; the dialog returns focus to the trigger. */
  close() {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();
  }

  /**
   * Closes the drawer when the click lands on the backdrop, outside the panel.
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    if (event.target === this.refs.dialog) this.close();
  }

  #handleClose = () => {
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  };
}

if (!customElements.get('lz-menu-drawer')) {
  customElements.define('lz-menu-drawer', LazarieeMenuDrawer);
}
